import { useMemo } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { r, s, type SpacingToken } from '@/theme/tokens';
import { SkeletonDataGrid } from '@/ui/composites/grid/SkeletonDataGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Box, Skeleton, StackedBarChartSkeleton, Surface } from '@/ui/primitives';
import type { GridColDef } from '@/ui/primitives';
import type { ReactElement } from 'react';

const HEADER_SECTION_GAP: SpacingToken = 'xl';
const HEADER_ROW_GAP: SpacingToken = 'md';
const HEADER_ACTIONS_GAP: SpacingToken = 'md';
const STATUS_CARDS_GAP: SpacingToken = 'md';
const STATUS_CARDS_PADDING_BOTTOM: SpacingToken = 'xs';
const METRIC_CARDS_GAP: SpacingToken = 'md';
const METRIC_CARDS_PADDING_BOTTOM: SpacingToken = 'xs';
const CHART_SECTION_GAP: SpacingToken = 'lg';
const CHART_TABS_GAP: SpacingToken = 'md';
const TABLE_SECTION_GAP: SpacingToken = 'lg';
const TABLE_TABS_GAP: SpacingToken = 'md';

export type ListPageSkeletonProps = {
  titleWidth?: number;
  showSearch?: boolean;
  showDateRange?: boolean;
  showChart?: boolean;
  showTabs?: boolean;
  tabCount?: number;
  statusCardCount?: number;
  metricCardCount?: number;
  tableRows?: number;
  chartHeight?: number;
  chartTabsCount?: number;
  chartTopGap?: SpacingToken;
  chartBottomGap?: SpacingToken;
  sectionGap?: SpacingToken;
  showToolbarSummary?: boolean;
};

export const ListPageSkeleton = ({
  titleWidth = 200,
  showSearch = false,
  showDateRange = false,
  showChart = false,
  showTabs = false,
  tabCount = 2,
  statusCardCount = 4,
  metricCardCount = 0,
  tableRows = 10,
  chartHeight = 200,
  chartTabsCount = 0,
  chartTopGap = 'none',
  chartBottomGap = 'none',
  sectionGap = 'xl',
  showToolbarSummary = false,
}: ListPageSkeletonProps): ReactElement => {
  const tableColumns = useMemo<Array<GridColDef<{ id: string }>>>(
    () => [
      { field: 'col-1', headerName: '', flex: 1.2 },
      { field: 'col-2', headerName: '', flex: 0.9 },
      { field: 'col-3', headerName: '', flex: 0.7 },
      { field: 'col-4', headerName: '', flex: 0.8 },
      { field: 'col-5', headerName: '', flex: 0.6 },
    ],
    [],
  );
  const showActions = showSearch || showDateRange;
  const cards = Array.from({ length: Math.min(statusCardCount, 6) });
  const metricCards = Array.from({ length: Math.min(metricCardCount, 3) });
  const tabSkeletons = showTabs ? Array.from({ length: tabCount }) : [];
  const chartTabSkeletons = chartTabsCount > 0 ? Array.from({ length: chartTabsCount }) : [];

  return (
    <Stack gap={sectionGap}>
      <Section>
        <Stack gap={HEADER_SECTION_GAP}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={HEADER_ROW_GAP}
            sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
          >
            <Skeleton variant="rounded" width={titleWidth} height={28} />
            {showActions ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={HEADER_ACTIONS_GAP}
                sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
              >
                {showSearch ? <Skeleton variant="rounded" height={28} width={150} /> : null}
                {showDateRange ? (
                  <Stack direction="row" gap={HEADER_ACTIONS_GAP} sx={{ alignItems: 'center' }}>
                    <Skeleton variant="rounded" height={28} width={100} />
                    <Skeleton variant="rounded" height={28} width={100} />
                    <Skeleton variant="rounded" height={28} width={100} />
                  </Stack>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
          {showToolbarSummary ? (
            <Box
              sx={{
                minHeight: (theme) => `calc(${theme.typography.pxToRem(16)} * 1.4)`,
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Skeleton
                variant="rounded"
                width="min(100%, 420px)"
                height={14}
                sx={{ borderRadius: r('pill') }}
              />
            </Box>
          ) : null}
          {statusCardCount > 0 ? (
            <Stack
              direction="row"
              wrap="nowrap"
              gap={STATUS_CARDS_GAP}
              align="stretch"
              sx={{ overflowX: 'auto', pb: s(STATUS_CARDS_PADDING_BOTTOM) }}
            >
              {cards.map((_, index) => (
                <Box
                  key={`status-card-${index}`}
                  sx={{ width: s('section'), flex: `0 0 ${s('section')}` }}
                >
                  <Surface
                    padding="none"
                    border={false}
                    sx={{
                      minHeight: componentSizes.cards.statusHeight,
                      height: componentSizes.cards.statusHeight,
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateRows: 'auto 1fr auto',
                        gap: s('sm'),
                        px: s('md'),
                        py: s('sm'),
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto',
                          alignItems: 'center',
                          gap: s('md'),
                          minWidth: 0,
                        }}
                      >
                        <Skeleton
                          variant="rounded"
                          width="68%"
                          height={componentSizes.dataGrid.skeletonHeight}
                          sx={{ borderRadius: r('pill') }}
                        />
                        <Skeleton variant="circular" width={24} height={24} />
                      </Box>
                      <Stack gap="xs">
                        <Skeleton
                          variant="rounded"
                          width="44%"
                          height={componentSizes.dataGrid.skeletonHeight}
                          sx={{ borderRadius: r('pill') }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="62%"
                          height={componentSizes.dataGrid.skeletonHeight}
                          sx={{ borderRadius: r('pill') }}
                        />
                      </Stack>
                      <Skeleton
                        variant="rounded"
                        width="52%"
                        height={componentSizes.dataGrid.skeletonHeight}
                        sx={{ borderRadius: r('pill') }}
                      />
                    </Box>
                  </Surface>
                </Box>
              ))}
            </Stack>
          ) : null}
          {metricCardCount > 0 ? (
            <Stack
              direction="row"
              wrap="nowrap"
              gap={METRIC_CARDS_GAP}
              align="stretch"
              sx={{ overflowX: 'auto', pb: s(METRIC_CARDS_PADDING_BOTTOM) }}
            >
              {metricCards.map((_, index) => (
                <Box key={`metric-card-${index}`} sx={{ width: s('4xl'), flex: `0 0 ${s('4xl')}` }}>
                  <Skeleton variant="rounded" height={componentSizes.cards.metricValueHeight} />
                </Box>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Section>
      {showChart ? (
        <Box sx={{ pt: s(chartTopGap), pb: s(chartBottomGap) }}>
          <Section paddingY="md">
            <Stack gap={CHART_SECTION_GAP}>
              {chartTabSkeletons.length > 0 ? (
                <Stack direction="row" gap={CHART_TABS_GAP} sx={{ alignItems: 'center' }}>
                  {chartTabSkeletons.map((_, index) => (
                    <Skeleton
                      key={`chart-tab-${index}`}
                      variant="rounded"
                      height={28}
                      width={index === 0 ? 80 : 96}
                    />
                  ))}
                </Stack>
              ) : null}
              <StackedBarChartSkeleton height={chartHeight} />
            </Stack>
          </Section>
        </Box>
      ) : null}
      <Section paddingY="md">
        <Stack gap={TABLE_SECTION_GAP}>
          {showTabs ? (
            <Stack direction="row" gap={TABLE_TABS_GAP} sx={{ alignItems: 'center' }}>
              {tabSkeletons.map((_, index) => (
                <Skeleton
                  key={`tab-${index}`}
                  variant="rounded"
                  height={28}
                  width={index === 0 ? 90 : 110}
                />
              ))}
            </Stack>
          ) : null}
          <SkeletonDataGrid<{ id: string }>
            columns={tableColumns}
            loadingRowCount={tableRows}
            pageSizeOptions={[tableRows]}
            pageSize={tableRows}
            getRowId={(row) => row.id}
            autoHeight
          />
        </Stack>
      </Section>
    </Stack>
  );
};
